/* eslint-disable no-param-reassign */

import {
  pick,
  forEach,
  merge,
} from 'lodash-es';

import getDefaultFields from './graphics-fields';

export const isLayerGroup = (element) => ['group', 'data-list', 'sheet-data', 'social-group', 'social-data', 'sportradar', 'marquee', 'shoppable'].includes(element.type);

export const matchesLayerTitle = (element, query, graphicFields = {}) => (
  (element.editable && element.name?.toLowerCase().includes(query))
  || ((isLayerGroup(element) || element.type === 'slideshow') && (
    element.elements?.some((child) => matchesLayerTitle(child, query, graphicFields))
    || element.entries?.some((child) => matchesLayerTitle(child, query, graphicFields))
  ))
  || (element.type === 'slideshow' && graphicFields[element.id]?.entries?.some(
    (entry) => entry.meta?.name?.toLowerCase().includes(query)
      || entry.fields?.some((field) => matchesLayerTitle(field, query, graphicFields)),
  ))
);

export const entriesMap = (elements, graphicFields) => elements.map((el) => {
  if (el.elements) {
    el.entries = entriesMap(el.elements, graphicFields);
  }

  const keys = Object.keys(getDefaultFields(el.type));
  const id = crypto.randomUUID();

  keys.push('name', 'type', 'editable');

  const obj = pick(el, keys);
  obj.template_id = el.id;
  obj.id = id;
  obj.visible = true;

  // Make sure element has any newly added fields we didn't get in the pick
  graphicFields[id] = {
    ...getDefaultFields(el.type),
    ...obj,
  };

  return obj;
});

// Merge field data into graphic element
// Not as details/janky as similar methods, but really just needed for metadata for logging
const mergeElementFields = (element, fields) => {
  const partial = fields[element.id];
  return merge({ visible: true }, element, partial || {});
};

// filter and flatten visible elements within groups so all elements accounted for
export const getVisibleEls = (elements, fields) => {
  const visibleEls = [];

  const getAllVisibleEls = (els) => {
    els.forEach((el) => {
      const elWithFields = mergeElementFields(el, fields);
      if (elWithFields.visible) visibleEls.push(elWithFields);
      if (el.elements?.length && elWithFields.visible) {
        getAllVisibleEls(el.elements);
      }
    });
  };

  getAllVisibleEls(elements);
  return visibleEls;
};

/**
 * Loop over elements and initialize `fields` on the root post for all of them.
 * SHOULD only be used for slideshows at this point, since other elements only populate fields when they are needed.
 * But slideshows suck and need them right from the start.
 * @param {Object} post - Timeline item.
 * @param {Array<Object>} elements - Graphic layers to generate fields for.
 */
export const setFields = (post, elements) => {
  if (!post || !elements) {
    return;
  }

  elements.forEach((element) => {
    const fields = {};
    fields.visible = true;

    forEach(getDefaultFields(element.type), (value, key) => {
      // Assign default if field is undefined
      fields[key] = element[key] || getDefaultFields(element.type)[key];
    });

    if (element.type === 'slideshow') {
      // Initialize first slide and build `fields` map for child layers.
      fields.entries = [{
        fields: entriesMap(element.elements, post.fields),
        meta: {
          name: 'Slide 1',
          collapsed: false,
          visible: true,
        },
      }];
    } else if (element.elements) {
      // Group of layers
      setFields(post, element.elements);
    }

    post.fields[element.id] = fields;
  });
};
